import { useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion } from "motion/react";

import { useTranslation } from "react-i18next";

import { CONTENT_KEYS } from "@/hooks/api/content.hooks";
import {
  FEED_KEYS,
  useCreateNotRecContent,
  useDeleteNotRecContent,
  useGetEpisodeHistory,
} from "@/hooks/api/feed.hooks";
import { useLang } from "@/hooks/useLang";
import { cn } from "@/utils/cn";
import { onError } from "@/utils/onError";
import { onSuccess } from "@/utils/onSuccess";

import { IconSlashCircle } from "../icons/main.icons";

const DetailNotRecMobileButton = ({ id, className }: { id: number; className?: string }) => {
  const { mutate: createNotRec, isPending: isCreatePending } = useCreateNotRecContent();
  const { mutate: deleteNotRec, isPending: isDeletePending } = useDeleteNotRecContent();
  const { t } = useTranslation();

  const queryClient = useQueryClient();

  const lang = useLang();

  const { data: history, isPending } = useGetEpisodeHistory(id);

  const isLoading = isPending || isCreatePending || isDeletePending;

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: FEED_KEYS.contentDetailHistory(lang, id) });
    await queryClient.invalidateQueries({ queryKey: CONTENT_KEYS.getRecommends(lang) });
  };

  const updateNotRec = () => {
    if (history?.not_recommended) {
      deleteNotRec(id, {
        onSuccess: async () => {
          await invalidate();
          onSuccess(t("NotRec.delete-success"));
        },
        onError,
      });
      return;
    }

    createNotRec(id, {
      onSuccess: async () => {
        await invalidate();
        onSuccess(t("NotRec.create-success"));
      },
      onError,
    });
  };

  return (
    <AnimatePresence mode="wait">
      {isPending ? (
        <div className="group flex flex-col items-center gap-2">
          <div className="size-6 animate-pulse rounded-lg bg-surface-bright"></div>
          <div className="h-5 w-[60%] animate-pulse rounded-lg bg-surface-bright"></div>
        </div>
      ) : (
        <motion.button
          type={"button"}
          exit={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          initial={{ opacity: 0 }}
          disabled={isLoading}
          onClick={updateNotRec}
          title={t("bookmarks-add")}
          key={history?.not_recommended.toString()}
          className={cn("group flex flex-col items-center gap-2 transition-all", className, {
            "opacity-50": isLoading,
          })}
        >
          <IconSlashCircle
            className={cn("size-6 stroke-on-primary", {
              "stroke-primary": history?.not_recommended,
            })}
          />
          <p className={cn("text-[14px] text-on-primary", { "text-primary": history?.not_recommended })}>
            {t("Actions.not-interesting")}
          </p>
        </motion.button>
      )}
    </AnimatePresence>
  );
};

export default DetailNotRecMobileButton;
